/**
 * Sign Up Page
 * User registration page with email/password and Google sign-in
 * 
 * FINLIST AUTH RULES:
 * - After successful sign-up, ALWAYS redirect to /dashboard
 * - Do NOT redirect back to previous route or welcome page
 * - This ensures a clear "entry into the app" experience
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import { isValidEmail, validatePassword } from '@/lib/utils';
import { LEGAL_VERSIONS, LEGAL_URLS } from '@/constants/legalVersions';

const SignUpPage = () => {
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [passwordValidation, setPasswordValidation] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  
  const { signUp, signInWithGoogle, currentUser, isAuthReady } = useAuth();
  const navigate = useNavigate();
  
  // RULE 4: Always redirect to dashboard after sign-up
  const postSignUpRoute = '/dashboard';

  // Redirect if already signed in
  useEffect(() => {
    if (currentUser) {
      navigate(postSignUpRoute, { replace: true });
    }
  }, [currentUser, navigate]);

  /**
   * Handle input change
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Validate password in real-time
    if (name === 'password') {
      const validation = validatePassword(value);
      setPasswordValidation(validation);
    }

    // Check password confirmation
    if (name === 'confirmPassword' || (name === 'password' && formData.confirmPassword)) {
      const passwordValue = name === 'password' ? value : formData.password;
      const confirmValue = name === 'confirmPassword' ? value : formData.confirmPassword;
      
      if (confirmValue && passwordValue !== confirmValue) {
        setErrors(prev => ({ ...prev, confirmPassword: 'Passwords do not match' }));
      } else {
        setErrors(prev => ({ ...prev, confirmPassword: '' }));
      }
    }
  };

  /**
   * Validate form data
   */
  const validateForm = () => {
    const newErrors = {};

    if (!formData.displayName.trim()) {
      newErrors.displayName = 'Name is required';
    } else if (formData.displayName.trim().length < 2) {
      newErrors.displayName = 'Name must be at least 2 characters';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else {
      const validation = validatePassword(formData.password);
      if (!validation.isValid) {
        newErrors.password = validation.feedback[0] || 'Password is too weak';
      }
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!acceptedTerms) {
      newErrors.terms = 'You must accept the Terms of Service and Privacy Policy';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Create acceptance metadata for legal compliance
      const acceptanceMetadata = {
        legalAcceptance: {
          acceptedTerms: true,
          acceptedPrivacy: true,
          acceptedAt: new Date().toISOString(),
          termsVersion: LEGAL_VERSIONS.terms,
          privacyVersion: LEGAL_VERSIONS.privacy
        }
      };

      await signUp(
        formData.email, 
        formData.password, 
        formData.displayName.trim(),
        acceptanceMetadata
      );
      // RULE 4: Always redirect to dashboard after sign-up
      navigate(postSignUpRoute, { replace: true });
    } catch (error) {
      console.error('Sign up error:', error);
      // Error is already handled in AuthContext
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle Google sign in
   */
  const handleGoogleSignIn = async () => {
    // Check terms acceptance first
    if (!acceptedTerms) {
      setErrors(prev => ({ ...prev, terms: 'Please check the box above to accept the Terms of Service and Privacy Policy' }));
      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    // Guard: Prevent rapid clicks
    if (isGoogleLoading) {
      return;
    }
    
    // Guard: Wait for auth to be ready
    if (!isAuthReady) {
      // Silent wait - button should already be disabled
      return;
    }

    setIsGoogleLoading(true);

    try {
      // Create acceptance metadata for legal compliance
      const acceptanceMetadata = {
        legalAcceptance: {
          acceptedTerms: true,
          acceptedPrivacy: true,
          acceptedAt: new Date().toISOString(),
          termsVersion: LEGAL_VERSIONS.terms,
          privacyVersion: LEGAL_VERSIONS.privacy
        }
      };

      // Pass source='signup' for production-safe logging
      const user = await signInWithGoogle(acceptanceMetadata, 'signup');
      
      // Only navigate if auth succeeded (null = blocked/skipped)
      if (user) {
        // RULE 4: Always redirect to dashboard after sign-up
        navigate(postSignUpRoute, { replace: true });
      }
    } catch (error) {
      console.error('Google sign in error:', error);
      // Error is already handled in AuthContext
    } finally {
      setIsGoogleLoading(false);
    }
  };

  /**
   * Get password strength color
   */
  const getPasswordStrengthColor = (score) => {
    if (score <= 2) return 'bg-red-500';
    if (score <= 3) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  /**
   * Get password strength text
   */
  const getPasswordStrengthText = (score) => {
    if (score <= 2) return 'Weak';
    if (score <= 3) return 'Medium';
    return 'Strong';
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-950 py-12 px-4 sm:px-6 lg:px-8 transition-colors">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">FinList</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Your smart personal finance companion</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Create Account</CardTitle>
            <CardDescription>
              Join FinList to manage your finances and save money.
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Auth Loading Notice */}
            {!isAuthReady && (
              <div className="text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-3 py-2 rounded-lg text-center">
                Initializing authentication...
              </div>
            )}

            {/* Terms & Privacy Acceptance - Placed at top for visibility */}
            <div className="space-y-1">
              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-800/50 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => {
                    setAcceptedTerms(e.target.checked);
                    if (errors.terms) {
                      setErrors(prev => ({ ...prev, terms: '' }));
                    }
                  }}
                  disabled={isLoading}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 dark:border-gray-600 dark:bg-slate-700"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  I agree to the{' '}
                  <Link
                    to={LEGAL_URLS.terms}
                    target="_blank"
                    className="text-brand-600 hover:text-brand-500 underline font-medium"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link
                    to={LEGAL_URLS.privacy}
                    target="_blank"
                    className="text-brand-600 hover:text-brand-500 underline font-medium"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Privacy Policy
                  </Link>
                </span>
              </label>
              {errors.terms && (
                <p className="text-xs text-red-600 dark:text-red-400 ml-3">{errors.terms}</p>
              )}
            </div>

            {/* Google Sign In Button */}
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleGoogleSignIn}
              disabled={isGoogleLoading || !isAuthReady}
              loading={isGoogleLoading || !isAuthReady}
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </Button>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-gray-300 dark:border-gray-700" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-gray-50 dark:bg-slate-950 px-2 text-gray-500 dark:text-gray-400">Or continue with</span>
              </div>
            </div>

            {/* Email/Password Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                name="displayName"
                type="text"
                label="Full Name"
                placeholder="Enter your full name"
                value={formData.displayName}
                onChange={handleChange}
                error={!!errors.displayName}
                errorMessage={errors.displayName}
                disabled={isLoading}
                autoComplete="name"
              />

              <Input
                name="email"
                type="email"
                label="Email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                errorMessage={errors.email}
                disabled={isLoading}
                autoComplete="email"
              />

              <div>
                <Input
                  name="password"
                  type="password"
                  label="Password"
                  placeholder="Create a password"
                  value={formData.password}
                  onChange={handleChange}
                  error={!!errors.password}
                  errorMessage={errors.password}
                  disabled={isLoading}
                  autoComplete="new-password"
                />
                
                {/* Password Strength Indicator */}
                {formData.password && (
                  <div className="mt-2">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-gray-600">Password strength:</span>
                      <span className={`text-xs font-medium ${
                        passwordValidation.score <= 2 ? 'text-red-600' :
                        passwordValidation.score <= 3 ? 'text-yellow-600' : 'text-green-600'
                      }`}>
                        {getPasswordStrengthText(passwordValidation.score)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          getPasswordStrengthColor(passwordValidation.score)
                        }`}
                        style={{ width: `${(passwordValidation.score / 5) * 100}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>

              <Input
                name="confirmPassword"
                type="password"
                label="Confirm Password"
                placeholder="Confirm your password"
                value={formData.confirmPassword}
                onChange={handleChange}
                error={!!errors.confirmPassword}
                errorMessage={errors.confirmPassword}
                disabled={isLoading}
                autoComplete="new-password"
              />

              <Button
                type="submit"
                className="w-full"
                disabled={isLoading || !acceptedTerms}
                loading={isLoading}
              >
                Create Account
              </Button>
            </form>

            {/* Sign In Link */}
            <div className="text-center">
              <span className="text-sm text-gray-600">
                Already have an account?{' '}
                <Link
                  to="/auth/signin"
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  Sign in
                </Link>
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-xs text-gray-500 dark:text-gray-400 space-y-2">
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Link to={LEGAL_URLS.terms} className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors">Terms</Link>
            <span>·</span>
            <Link to={LEGAL_URLS.privacy} className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors">Privacy</Link>
            <span>·</span>
            <Link to={LEGAL_URLS.cookies} className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors">Cookies</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUpPage;